import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, Truck, BadgeCheck, ShoppingBag } from 'lucide-react';
import { useProducts } from '@/contexts/ProductsContext';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { trackAddToCart } from '@/lib/tracking';

// Price + Buy Now / Add to Cart for the 3-Step Set, shown in the hero and the
// closing CTA of /3-step-treatment so visitors from ads can buy right away.
// Live price comes from routineSet (same source as the Products page); the
// locale strings are only a fallback while products load.
export function useBuyThreeStepSet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { routineSet } = useProducts();
  const { addToCart, openCart } = useCart();
  const { showToast } = useToast();

  const price = routineSet?.price ?? t('threeStep.cta.price');
  const originalPrice = routineSet?.originalPrice ?? t('threeStep.cta.originalPrice');

  const add = () => {
    addToCart('3-step-set', 1);
    if (routineSet) {
      trackAddToCart({ id: '3-step-set', name: routineSet.name, price: Number(routineSet.price.replace(/[^0-9.]/g, '')), quantity: 1 });
    }
  };

  return {
    price,
    originalPrice,
    addToCart: () => {
      add();
      showToast(t('cart.added', 'Added to Cart ✓'), t('cart.viewCart', 'View Cart →'), openCart);
    },
    buyNow: () => {
      add();
      navigate(prefix + '/checkout');
    },
  };
}

export default function ThreeStepBuyBox({ align = 'left' }: { align?: 'left' | 'center' }) {
  const { t } = useTranslation();
  const { price, originalPrice, addToCart, buyNow } = useBuyThreeStepSet();
  const center = align === 'center';

  return (
    <div
      className={`w-full max-w-md ${center ? 'mx-auto' : ''} rounded-[22px] border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 md:p-6 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.6)]`}
    >
      <p className={`text-[10.5px] tracking-[0.22em] uppercase text-[#C9A27A] font-semibold ${center ? 'text-center' : ''}`}>
        {t('threeStep.buy.setLabel')}
      </p>
      <div className={`mt-2 flex items-end gap-3 flex-wrap ${center ? 'justify-center' : ''}`}>
        <span className="font-serif text-4xl md:text-[44px] leading-none font-bold text-white">{price}</span>
        <span className="text-base text-white/35 line-through pb-1">{originalPrice}</span>
        <span className="mb-1 px-2.5 py-1 rounded-full bg-[#2D6A4F]/25 text-[#7FD1A8] text-[11px] font-semibold tracking-wide">
          {t('threeStep.cta.save')}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <button
          onClick={buyNow}
          className="py-3.5 rounded-full bg-[#7B1113] text-white text-[12.5px] font-semibold tracking-[0.14em] uppercase hover:bg-[#5C0D0F] active:scale-[0.98] transition-all cursor-pointer shadow-[0_10px_28px_-10px_rgba(123,17,19,0.9)]"
        >
          {t('common.buyNow')}
        </button>
        <button
          onClick={addToCart}
          className="py-3.5 rounded-full border border-white/25 text-white text-[12.5px] font-semibold tracking-[0.14em] uppercase hover:bg-white/10 active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-2"
        >
          <ShoppingBag className="w-4 h-4" /> {t('common.addToCart')}
        </button>
      </div>

      <div className={`mt-5 pt-4 border-t border-white/10 flex flex-wrap gap-x-5 gap-y-2 text-[11.5px] text-white/55 ${center ? 'justify-center' : ''}`}>
        <span className="flex items-center gap-1.5"><Gift className="w-3.5 h-3.5 text-[#C9A27A]" /> {t('threeStep.buy.giftBox')}</span>
        <span className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-[#C9A27A]" /> {t('threeStep.buy.cod')}</span>
        <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-[#C9A27A]" /> {t('threeStep.buy.madeInKorea')}</span>
      </div>
    </div>
  );
}
